import { formatDistanceToNowStrict } from 'date-fns';
import { Check } from 'lucide-react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  ToolTip,
  focusVisibleStyles
} from 'pixelize-design-library';
import type { Locale } from 'date-fns';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useDateLocale } from '@core/i18n/dateLocale';
import type { NotificationRow } from '@core/store/Notifications/notificationTypes';
import { mutedText, ruleBorder } from '@core/Theme/tokens';
import { useAppTheme } from '@core/Theme/useAppTheme';
import { StatusTag } from '@modules/shared/components/ui';

import { accentColorsForType, iconForType } from '../notificationDisplay';

const SR_ONLY = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap'
} as const;

function relativeTime(value: string, locale: Locale): string | null {
  const at = Date.parse(value);
  if (Number.isNaN(at)) return null;
  return formatDistanceToNowStrict(new Date(Math.min(at, Date.now())), { addSuffix: true, locale });
}

interface UserFeedRowProps {
  row: NotificationRow;
  testId: string;
  compact?: boolean;
  onOpen: () => void;
  onMarkRead?: () => void;
}

export function UserFeedRow({
  row,
  testId,
  compact = false,
  onOpen,
  onMarkRead
}: UserFeedRowProps) {
  const { t } = useTranslation();
  const { colors } = useAppTheme();
  const { ink, fill } = accentColorsForType(row.type, colors);
  const unread = !row.is_read;
  const dateLocale = useDateLocale();
  const when = relativeTime(row.created_at, dateLocale);

  const body: ReactNode = (
    <>
      <Flex
        flexShrink={0}
        boxSize={compact ? '28px' : '36px'}
        align='center'
        justify='center'
        borderRadius='8px'
        bg={fill}
        color={ink}
        aria-hidden
      >
        {iconForType(row.type)}
      </Flex>
      <Box flex='1 1 200px' minW={0} textAlign='left'>
        <Flex align='center' gap={2}>
          {unread && (
            <Box
              boxSize='8px'
              borderRadius='full'
              bg={colors.primary[500]}
              flexShrink={0}
              aria-hidden
            />
          )}
          <Text fontSize='sm' fontWeight={unread ? 700 : 500} noOfLines={1}>
            {unread && <Box as='span' sx={SR_ONLY}>{`${t('notifUi.unreadPrefix')}: `}</Box>}
            {row.title}
          </Text>
        </Flex>
        {row.description ? (
          <Text fontSize='xs' color={mutedText(colors)} noOfLines={compact ? 1 : 2} mt='2px'>
            {row.description}
          </Text>
        ) : null}
        {row.section || (compact && when) ? (
          <Flex gap={2} mt={1} align='center' wrap='wrap'>
            {row.section ? <StatusTag tone='neutral' label={t(`dashHub.${row.section}`)} /> : null}
            {compact && when ? (
              <Text fontSize='xs' color={mutedText(colors)}>
                {when}
              </Text>
            ) : null}
          </Flex>
        ) : null}
      </Box>
    </>
  );

  if (compact) {
    return (
      <Flex
        as='button'
        type='button'
        w='100%'
        gap={3}
        px={4}
        py={3}
        align='flex-start'
        borderBottomWidth='1px'
        borderColor={ruleBorder(colors)}
        _hover={{ bg: colors.backgroundColor.secondary }}
        sx={focusVisibleStyles(colors)}
        onClick={onOpen}
        data-testid={testId}
        data-read={row.is_read ? 'true' : 'false'}
      >
        {body}
      </Flex>
    );
  }

  return (
    <Flex
      as='li'
      gap={3}
      px={5}
      py={3}
      align={{ base: 'flex-start', md: 'center' }}
      wrap={{ base: 'wrap', md: 'nowrap' }}
      borderBottomWidth='1px'
      borderColor={ruleBorder(colors)}
      _last={{ borderBottomWidth: 0 }}
      data-testid={testId}
      data-read={row.is_read ? 'true' : 'false'}
    >
      {body}
      {when ? (
        <Text fontSize='xs' color={mutedText(colors)} flexShrink={0}>
          {when}
        </Text>
      ) : null}
      <Flex gap={1} flexShrink={0} align='center'>
        {row.link ? (
          <Button
            size='sm'
            variant={row.needs_you && unread ? 'solid' : 'outline'}
            colorScheme='primary'
            aria-label={t('notifUi.openAria', { title: row.title })}
            onClick={onOpen}
            data-testid={`${testId}-open`}
          >
            {t('common.open')}
          </Button>
        ) : null}
        {unread && onMarkRead ? (
          <ToolTip label={t('notifUi.markRead')}>
            <IconButton
              size='sm'
              variant='ghost'
              aria-label={t('notifUi.markReadAria', { title: row.title })}
              icon={<Check size={16} />}
              onClick={onMarkRead}
              data-testid={`${testId}-mark-read`}
            />
          </ToolTip>
        ) : null}
      </Flex>
    </Flex>
  );
}
